/**
 * Central tab component
 */
//  Generic imports
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

//  Motif imports
import { MotifTooltip} from "@ey-xd/motif-react";
import MotifTable from '@ey-xd/motif-react/Table';
import MotifToast from '@ey-xd/motif-react/Toast';

//  Icons imports
import iconCheckCircleSuccess from '../../../assets/images/check-circle.svg';
import unpublished from '../../../assets/images/unpublished.svg';
import published from '../../../assets/images/published.svg';

//  Redux imports
import { setTableTypes as setTableTypesToStore, resetNewCentralPrompt, setSelectedCentralPrompt } from "../../../redux/promptSlice";

//  Internal imports
import './PersonalPromptList.scss';
import PromptLibraryService from '../../../services/promptLibraryService';
import LoaderComponent from '../../molecules/loader/LoaderComponent';
import AddCentralPrompt from '../../molecules/modals/addCentralPrompt/AddCentralPrompt';

function CentralTab() {
    const [tableTypes, setTableTypes] = useState([]);
    const [isLoadingInProgress, setIsLoadingInProgress] = useState(false);
    const { user } = useSelector(state => state.user);
    const { defaultNewCentralPrompt, newCentralPrompt } = useSelector(state => state.prompt);
    const [showAddCentralPromptModal, setShowAddCentralPromptModal] = useState(false);
    const [showPromptAddSuccessToast, setShowPromptAddSuccessToast] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const colDefs = [
        {
            field: "tableType",
            headerName: "Table Type",
            wrapText: true,
            autoHeight: true,
            flex: 2,
            filter: false,
            sortable: false
        },
        {
            field: "promptName",
            headerName: "Prompt Name",
            wrapText: true,
            autoHeight: true,
            flex: 2,
            filter: false,
            sortable: false
        },
        {
            colId: "status",
            field: "isPublished",
            headerName: "Status",
            wrapText: true,
            autoHeight: true,
            width: 110,
            filter: false,
            sortable: false,
            cellRenderer: (params) => {
                const isPublished = params.data.isPublished === true || params.data.isPublished === 1;
                if (!isPublished) {
                    return (
                        <MotifTooltip
                            trigger={
                                <img
                                    src={unpublished}
                                    alt="unpublished"                                    className="unpublished"
                                />
                            }
                            allowClick={false}
                            placement="left"
                            hideCloseButton={true}
                        >
                            Table Type has been unpublished.<br /> No actions available
                        </MotifTooltip>
                    );
                }
                return (
                    <img
                        src={published}
                        alt="Published"
                        className="published"
                    />
                );
            }
        }
    ];
    const promptLibraryService = new PromptLibraryService();

    useEffect(() => {
        if(user) {
            getTableTypes(user.username)
        }
        // eslint-disable-next-line
    }, []);

    /**
     * Function to get personal prompts associated with user
     */
    const getTableTypes = async (userId) => {
        setIsLoadingInProgress(true);
        const vData = { ProjectScopeId: "", Type: "A", application: "EXL_AddIn", PromptType: "Central" };
        const vTableTypes = await promptLibraryService.getTableTypes(vData);
        if (!vTableTypes || !Array.isArray(vTableTypes)) {
            setIsLoadingInProgress(false);
            return;
        }
        const formattedTableTypes = vTableTypes.map((prompt, index) => ({
            ...prompt,
            id: prompt?.id || `generated-${index}`,
            name: prompt.promptName || "Unnamed Prompt",
            tableType: prompt.api || "Unknown Type",
            isPublished:prompt.isPublished
        }));
        setTableTypes(formattedTableTypes);
        dispatch(setTableTypesToStore({
            tableTypes: formattedTableTypes
        }));    
        setIsLoadingInProgress(false);
    };
    

    const handleCloseAddCentralPromptModal = () => {
        dispatch(resetNewCentralPrompt({ newCentralPrompt: defaultNewCentralPrompt }));
        setShowAddCentralPromptModal(false);
    }

    const handleConfirmAddCentralPromptModal = async () => {
        let newCentralPromptToAdd = JSON.parse(JSON.stringify(newCentralPrompt));
        newCentralPromptToAdd.createdBy = user.username;
        newCentralPromptToAdd.prompts.map((p) => delete p.promptMethodName);
        setIsLoadingInProgress(true);
        const response = await promptLibraryService.addCentralPrompt(newCentralPromptToAdd);
        dispatch(resetNewCentralPrompt({ newCentralPrompt: defaultNewCentralPrompt }));
        setShowAddCentralPromptModal(false);
        setIsLoadingInProgress(false);
        if(user) {
            getTableTypes(user.username);
        }
        if(response?.status === 200) {
            setShowPromptAddSuccessToast(true);
        }
    }
   
    const handleCellClick = (e) => {
        if (e.column.colId !== "actions") {
            dispatch(setSelectedCentralPrompt({ selectedCentralPrompt: e.data }));
            navigate(generatePath(":id", {id: e.data.id}));          
        }
    }    

    return (
        <React.Fragment>
            <LoaderComponent show={isLoadingInProgress}/>
            <div className="personalPromptContainer">
                <div className="view-prompt-table">
                    <MotifTable
                        id="personalPromptTable"
                        rowData={tableTypes}
                        columnDefs={colDefs}
                        suppressCellFocus
                        suppressRowClickSelection
                        suppressRowHoverHighlight
                        suppressNoRowsOverlay
                        onCellClicked={handleCellClick}
                        className='personalPromptTable'
                        getRowClass={(params) => {
                            return params.data.isPublished === true || params.data.isPublished === 1
                                ? ''
                                : 'disabled-row';
                        }}>
                    </MotifTable>
                    
                    {
                        showPromptAddSuccessToast &&
                        <MotifToast
                            variant="success"
                            onClose={() => {
                                setShowPromptAddSuccessToast(false);
                            }}
                            icons={{toast:<img src={iconCheckCircleSuccess} alt='Success' />}}
                            className='notifyToast'
                        >
                            Prompt added successfully
                        </MotifToast>
                    }
                </div>
            </div>
            
            <AddCentralPrompt
                showAddCentralModal={showAddCentralPromptModal}
                handleCloseAddCentralPromptModal={handleCloseAddCentralPromptModal}
                handleConfirmAddCentralPromptModal={handleConfirmAddCentralPromptModal} />
        </React.Fragment>
    );
}

export default CentralTab;